import { liveQuery } from "dexie";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { calculateFinalPayableRounding } from "../runtime/commerce/final-payable-rounding";
import { resolveLocalSaleFinalPayable } from "../runtime/commerce/local-sale-final-payable";
import type { CanonicalLoyaltyEarnAck } from "../runtime/loyalty/loyalty-earn-ack";
import { resolveLocalLoyaltyEarnPreview } from "../runtime/loyalty/local-loyalty-resolver";
import {
  localCartSaleFingerprint,
  readLocalCart,
  type LocalCartDraft,
} from "../runtime/local-store/cart-state";
import { readCommerceCalculationRuntime } from "../runtime/local-store/commerce-calculation-runtime";
import { readLocalLoyaltyRedemption, type LocalLoyaltyRedemption } from "../runtime/local-store/loyalty-redemption-state";
import { readPublishedLoyaltyBaseEarnRuntime } from "../runtime/local-store/loyalty-runtime";
import { posRuntimeDb } from "../runtime/local-store/pos-db";
import { readTerminalCredential, type TerminalCredentialState } from "../runtime/local-store/terminal-state";

type SaleJournalSnapshot = {
  schema?: string;
  selectedMember?: { customerId?: string; membershipId?: string; customerName?: string } | null;
  canonical?: {
    order_id?: string;
    order_no?: string;
    order_status?: string;
    loyalty_earn?: CanonicalLoyaltyEarnAck | null;
  } | null;
};

type Snapshot = {
  terminal: TerminalCredentialState | null;
  cart: LocalCartDraft | null;
  loyaltyRedemption: LocalLoyaltyRedemption | null;
  loyalty: Awaited<ReturnType<typeof readPublishedLoyaltyBaseEarnRuntime>>;
  commerceCalculation: Awaited<ReturnType<typeof readCommerceCalculationRuntime>>;
  latestSale: SaleJournalSnapshot | null;
};

function formatPoints(value: number) {
  return new Intl.NumberFormat("th-TH", { maximumFractionDigits: 2 }).format(value);
}

function saleScope(terminal: TerminalCredentialState | null) {
  const identity = terminal?.identity;
  return identity ? `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}` : "";
}

export function LoyaltyPaymentContextHost() {
  const [mountNode, setMountNode] = useState<Element | null>(null);
  const [openedAt, setOpenedAt] = useState<number | null>(null);
  const [snapshot, setSnapshot] = useState<Snapshot>({
    terminal: null,
    cart: null,
    loyaltyRedemption: null,
    loyalty: null,
    commerceCalculation: null,
    latestSale: null,
  });
  const [lastActiveCart, setLastActiveCart] = useState<LocalCartDraft | null>(null);

  useEffect(() => {
    let previous: Element | null = null;
    const update = () => {
      const next = document.querySelector(".cash-sale-modal__total");
      if (next !== previous) {
        previous = next;
        setMountNode(next);
        if (next) setOpenedAt(Date.now());
        else {
          setOpenedAt(null);
          setLastActiveCart(null);
        }
      }
    };
    update();
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mountNode || openedAt == null) return;
    const subscription = liveQuery(async () => {
      const terminal = await readTerminalCredential();
      const identity = terminal?.identity ?? null;
      const [cart, loyalty, commerceCalculation] = identity
        ? await Promise.all([
            readLocalCart(identity),
            readPublishedLoyaltyBaseEarnRuntime(identity),
            readCommerceCalculationRuntime(identity),
          ])
        : [null, null, null];
      const loyaltyRedemption = identity && cart
        ? await readLocalLoyaltyRedemption(identity, cart.requestKey)
        : null;

      let latestSale: SaleJournalSnapshot | null = null;
      const scopeKey = saleScope(terminal);
      if (scopeKey) {
        const rows = await posRuntimeDb.journal
          .where("scopeKey")
          .equals(scopeKey)
          .and((row) => row.kind === "SALE_ORDER" && Date.parse(row.createdAt) >= openedAt - 1_000)
          .sortBy("createdAt");
        const row = rows.length ? rows[rows.length - 1] : undefined;
        if (row?.snapshot && typeof row.snapshot === "object") latestSale = row.snapshot as SaleJournalSnapshot;
      }
      return { terminal, cart, loyaltyRedemption, loyalty, commerceCalculation, latestSale } satisfies Snapshot;
    }).subscribe({
      next: setSnapshot,
      error: () => setSnapshot((current) => ({ ...current, latestSale: null })),
    });
    return () => subscription.unsubscribe();
  }, [mountNode, openedAt]);

  useEffect(() => {
    if (snapshot.cart?.lines.length) setLastActiveCart(snapshot.cart);
  }, [snapshot.cart]);

  const cart = snapshot.cart?.lines.length ? snapshot.cart : lastActiveCart;
  const canonical = snapshot.latestSale?.canonical ?? null;
  const earn = canonical?.loyalty_earn ?? null;
  const selectedMember = snapshot.latestSale?.selectedMember ?? cart?.selectedMember ?? null;
  const isCredit = snapshot.latestSale?.schema === "LOCAL_CREDIT_SALE_V1";

  const preview = useMemo(() => {
    if (!cart || !snapshot.loyalty) return null;
    const quote = cart.quote && cart.quote.cartFingerprint === localCartSaleFingerprint(cart) ? cart.quote : null;
    const calculation = cart.commerceCalculation ?? snapshot.commerceCalculation;
    if (!quote || !calculation) return null;
    const currencyCode = quote.currencyCode ?? cart.lines[0]?.currencyCode ?? "THB";
    const basePayable = calculateFinalPayableRounding(quote.postPromotionTotal, calculation);
    let payable = basePayable;
    try {
      payable = resolveLocalSaleFinalPayable(basePayable, snapshot.loyaltyRedemption, cart, currencyCode);
    } catch {
      return null;
    }
    return resolveLocalLoyaltyEarnPreview({
      cart,
      quote,
      runtime: snapshot.loyalty,
      finalPayableTotal: payable.totalAmount,
    });
  }, [cart, snapshot.commerceCalculation, snapshot.loyalty, snapshot.loyaltyRedemption]);

  if (!mountNode) return null;

  let title = "";
  let detail: string | null = null;
  let tone: "ready" | "neutral" | "pending" = "neutral";

  if (earn?.committed) {
    const earned = Number(earn.earn_result?.total_points ?? 0);
    const balance = earn.point_summary?.current_balance;
    title = earned > 0 ? `ได้รับครั้งนี้ ${formatPoints(earned)} แต้ม` : "บิลนี้ไม่ได้รับแต้ม";
    detail = typeof balance === "number" && Number.isFinite(balance)
      ? `แต้มคงเหลือ ${formatPoints(balance)} แต้ม`
      : null;
    tone = earned > 0 ? "ready" : "neutral";
  } else if (canonical && isCredit) {
    title = "ยังไม่สะสมแต้มจนกว่าจะรับเงินจริง";
    detail = "รายการค้างชำระจะให้ Loyalty Core ยืนยันแต้มเมื่อมีการชำระหนี้จริง";
    tone = "neutral";
  } else if (snapshot.latestSale && !canonical) {
    title = selectedMember ? "ชำระสำเร็จ · แต้มรอยืนยันหลังซิงก์" : "ชำระสำเร็จ";
    detail = selectedMember ? "ระบบจะใช้คำขอเดิมยืนยันกับ Loyalty Core โดยไม่เพิ่มแต้มซ้ำ" : null;
    tone = "pending";
  } else if (!selectedMember) {
    title = "เลือกสมาชิกเพื่อสะสมแต้ม";
    detail = null;
    tone = "neutral";
  } else if (!preview?.ready) {
    title = "แต้มจะยืนยันตอนชำระ";
    detail = preview?.reasonCode === "LOYALTY_POLICY_NOT_READY"
      ? "กำลังเตรียม Published Loyalty Policy ในเครื่อง"
      : "บิลนี้ต้องใช้ข้อมูลเพิ่มก่อนยืนยันแต้ม ระบบจะไม่เดาตัวเลข";
    tone = "pending";
  } else if (preview.previewTotalPoints > 0) {
    title = `คาดว่าจะได้รับ ${formatPoints(preview.previewTotalPoints)} แต้ม`;
    detail = null;
    tone = "ready";
  } else {
    title = "บิลนี้ไม่เข้าเงื่อนไขสะสมแต้ม";
    detail = null;
    tone = "neutral";
  }

  return createPortal(
    <div className={`cash-sale-modal__loyalty cash-sale-modal__loyalty--${tone}`} role="status" aria-live="polite">
      <strong>{title}</strong>
      {detail ? <span>{detail}</span> : null}
    </div>,
    mountNode,
  );
}
